const read = (value: string | undefined) => value?.trim() || "";

export const env = {
  supabaseUrl: read(process.env.NEXT_PUBLIC_SUPABASE_URL),
  supabaseAnonKey: read(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY),
  supabaseSecretKey: read(process.env.SUPABASE_SECRET_KEY) || read(process.env.SUPABASE_SERVICE_ROLE_KEY),
  sealToken: read(process.env.SEAL_SUBSCRIPTIONS_TOKEN),
  sealBaseUrl: read(process.env.SEAL_SUBSCRIPTIONS_BASE_URL) || "https://app.sealsubscriptions.com",
  // Shopify Storefront API — used only by the admin-only in-app subscription
  // checkout (beta). Public checkout flow does not depend on these.
  shopifyStorefrontToken: read(process.env.SHOPIFY_STOREFRONT_API_TOKEN),
  shopifyStoreDomain: read(process.env.SHOPIFY_STORE_DOMAIN),
  // Shopify Admin API (`write_discounts` scope) — used only to mint the
  // single-use first-cycle credit codes for in-app plan upgrades. OPTIONAL:
  // when no admin credential is configured, upgrades still work but without the
  // unused-time credit. Two supported credential styles (either one works):
  //   - a long-lived custom-app token in SHOPIFY_ADMIN_API_TOKEN, OR
  //   - a Dev-Dashboard app's client id + secret, which the app exchanges for a
  //     short-lived token via the client_credentials grant (auto-refreshed).
  shopifyAdminToken: read(process.env.SHOPIFY_ADMIN_API_TOKEN),
  shopifyAdminClientId: read(process.env.SHOPIFY_ADMIN_CLIENT_ID),
  shopifyAdminClientSecret: read(process.env.SHOPIFY_ADMIN_CLIENT_SECRET),
};

export function hasPublicSupabaseEnv() {
  return Boolean(env.supabaseUrl && env.supabaseAnonKey);
}

export function requirePublicSupabaseEnv() {
  if (!hasPublicSupabaseEnv()) {
    throw new Error(
      "Missing Supabase public environment variables. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.",
    );
  }

  return {
    supabaseUrl: env.supabaseUrl,
    supabaseAnonKey: env.supabaseAnonKey,
  };
}

export function requireServiceRoleEnv() {
  const publicEnv = requirePublicSupabaseEnv();
  if (!env.supabaseSecretKey) {
    throw new Error("Missing SUPABASE_SECRET_KEY.");
  }

  return {
    ...publicEnv,
    supabaseServiceRoleKey: env.supabaseSecretKey,
  };
}

export function hasSealSubscriptionsEnv() {
  return Boolean(env.sealToken);
}

export function requireSealSubscriptionsEnv() {
  if (!env.sealToken) {
    throw new Error("Missing SEAL_SUBSCRIPTIONS_TOKEN.");
  }

  return {
    sealToken: env.sealToken,
    sealBaseUrl: env.sealBaseUrl,
  };
}

export function hasShopifyAdminEnv() {
  if (!env.shopifyStoreDomain) return false;
  return Boolean(
    env.shopifyAdminToken || (env.shopifyAdminClientId && env.shopifyAdminClientSecret),
  );
}

export function requireShopifyAdminEnv() {
  if (!hasShopifyAdminEnv()) {
    throw new Error(
      "Missing Shopify Admin credentials: set SHOPIFY_ADMIN_API_TOKEN, or SHOPIFY_ADMIN_CLIENT_ID + SHOPIFY_ADMIN_CLIENT_SECRET (plus SHOPIFY_STORE_DOMAIN).",
    );
  }
  return {
    shopifyStoreDomain: env.shopifyStoreDomain,
    shopifyAdminToken: env.shopifyAdminToken,
    shopifyAdminClientId: env.shopifyAdminClientId,
    shopifyAdminClientSecret: env.shopifyAdminClientSecret,
  };
}

export function requireShopifyStorefrontEnv() {
  if (!env.shopifyStorefrontToken || !env.shopifyStoreDomain) {
    throw new Error("Missing SHOPIFY_STOREFRONT_API_TOKEN or SHOPIFY_STORE_DOMAIN.");
  }

  return {
    shopifyStorefrontToken: env.shopifyStorefrontToken,
    shopifyStoreDomain: env.shopifyStoreDomain,
  };
}
